import React, { useState, useEffect } from "react";
import axios from "axios";
import { Drawer, message, Popconfirm, Select, Alert } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  InputItem,
  InputNumberItem,
  ButtonItem,
  SelectItem,
  SelectInsumosItem,
} from "../../../../../Components/Items";

const { Option } = Select;

//Panel lateral de una habitación: sus datos (número, nombre, capacidad, insumo de tarifa y a qué piso
//pertenece) y el borrado. La geometría no se edita aquí: se arrastra y se redimensiona en el lienzo.
//Molde: MapaAlmacenEditor/Components/PanelPropiedadesRack.js.
const PanelPropiedadesHabitacion = ({
  visible,
  onClose,
  habitacion,
  niveles,
  empresaId,
  headersToken,
  onGuardado,
  onEliminado,
}) => {
  const [numero, setNumero] = useState("");
  const [nombre, setNombre] = useState("");
  const [capacidad, setCapacidad] = useState(undefined);
  const [observaciones, setObservaciones] = useState("");
  const [insumoId, setInsumoId] = useState(undefined);
  const [nivelHotelId, setNivelHotelId] = useState(undefined);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!habitacion) return;
    setNumero(habitacion.numero || "");
    setNombre(habitacion.nombre || "");
    setCapacidad(habitacion.capacidad);
    setObservaciones(habitacion.observaciones || "");
    setInsumoId(habitacion.insumoId ? String(habitacion.insumoId) : undefined);
    setNivelHotelId(habitacion.nivelHotelId ? String(habitacion.nivelHotelId) : undefined);
  }, [habitacion]);

  const guardar = async () => {
    if (!String(numero).trim()) {
      message.warning("La habitación necesita un número");
      return;
    }
    setGuardando(true);
    try {
      const res = await axios.post(
        "hoteleria/editarHabitacion",
        {
          empresaId,
          _id: habitacion._id,
          nivelHotelId,
          // Cadena vacía y no undefined: el backend solo aplica los campos que vienen en el body, así
          // que undefined significaría "no lo toques" y sería imposible quitarle el insumo.
          insumoId: insumoId || "",
          numero,
          nombre,
          capacidad,
          observaciones,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Habitación actualizada correctamente");
        onGuardado();
      } else {
        message.error(res.data.error || "No se pudo actualizar la habitación");
      }
    } catch (error) {
      console.error("Error al actualizar la habitación:", error);
      message.error("No se pudo actualizar la habitación");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      const res = await axios.post(
        "hoteleria/eliminarHabitacion",
        { empresaId, _id: habitacion._id },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Habitación eliminada correctamente");
        onEliminado();
      } else {
        message.error(res.data.error || "No se pudo eliminar la habitación");
      }
    } catch (error) {
      console.error("Error al eliminar la habitación:", error);
      message.error("No se pudo eliminar la habitación");
    }
  };

  if (!habitacion) return null;

  return (
    <Drawer
      title={`Habitación ${habitacion.numero || ""}`}
      visible={visible}
      onClose={onClose}
      width={380}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Número</div>
          <InputItem value={numero} onChange={(e) => setNumero(e.target.value)} />
        </div>
        <div style={{ width: 110 }}>
          <div style={{ marginBottom: 4, color: "#6b7280" }}>Capacidad</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={capacidad} onChange={setCapacidad} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Nombre (opcional)</div>
        <InputItem
          value={nombre}
          placeholder="Suite Matrimonial..."
          onChange={(e) => setNombre(e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Piso</div>
        <SelectItem width="100%" value={nivelHotelId} onChange={setNivelHotelId}>
          {(niveles || []).map((valorNivel) => (
            <Option key={valorNivel._id} value={String(valorNivel._id)}>
              {valorNivel.nombre}
            </Option>
          ))}
        </SelectItem>
        <div style={{ color: "#8a93a6", fontSize: 12, marginTop: 4 }}>
          Al cambiarla de piso conserva sus coordenadas, así que aparecerá en la misma posición del
          nuevo lienzo.
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Insumo de la tarifa</div>
        <SelectInsumosItem
          empresaId={empresaId}
          headersToken={headersToken}
          value={insumoId}
          onChange={setInsumoId}
          width="100%"
          placeholder="Buscar insumo..."
          allowClear
        />
      </div>

      {!insumoId && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Sin insumo de tarifa esta habitación se pinta gris en el mapa y no aparece al capturar una venta."
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Observaciones</div>
        <InputItem value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Popconfirm
          title="¿Eliminar esta habitación? Las estancias que ya se le registraron se conservan."
          onConfirm={eliminar}
          okText="Eliminar"
          cancelText="Cancelar"
        >
          <ButtonItem danger icon={<DeleteOutlined />}>
            Eliminar
          </ButtonItem>
        </Popconfirm>
        <ButtonItem type="primary" loading={guardando} onClick={guardar}>
          Guardar
        </ButtonItem>
      </div>
    </Drawer>
  );
};

export default PanelPropiedadesHabitacion;
